import { Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { QuotesService } from '../quotes.service';
import { AsyncPipe, CurrencyPipe, DatePipe, NgClass } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import {
  BehaviorSubject,
  debounceTime,
  distinctUntilChanged,
  Observable,
  Subject,
  Subscription,
} from 'rxjs';
import { Quote } from '../../models/quote.model';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SharedConfirmDialogComponent } from '@tangible/shared';
import { ConfirmationService } from 'primeng/api';
import { PopoverModule } from 'primeng/popover';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { SettingsService } from '../settings-page/settings-service/settings.service';
import { Settings } from '../../models/settings.model';
import { FormsModule } from '@angular/forms';
import { SortableColumnHeaderComponent } from './sortable-column-header/sortable-column-header.component';
import { CreatedDateRangeHeaderComponent } from './created-date-range-header/created-date-range-header.component';

@Component({
  selector: 'app-quotes-list-page',
  imports: [
    AsyncPipe,
    DatePipe,
    CurrencyPipe,
    ButtonModule,
    CheckboxModule,
    RouterModule,
    SharedConfirmDialogComponent,
    PopoverModule,
    NgClass,
    Paginator,
    FormsModule,
    SortableColumnHeaderComponent,
    CreatedDateRangeHeaderComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './quotes-list-page.component.html',
  styleUrl: './quotes-list-page.component.scss',
})
export class QuotesListPageComponent implements OnInit, OnDestroy {
  quotesService = inject(QuotesService);
  settingsService = inject(SettingsService);
  router = inject(Router);
  confirmationService = inject(ConfirmationService);
  showArchivedQuotes = false;
  creatingQuote = signal(false);
  expandedQuotes = new Set<string>();
  settings: Settings | undefined;

  // Pagination state
  totalRecords = 0;
  rows = 20;
  first = 0;

  protected searchSubject = new Subject<string>();
  searchTerm = '';

  numberOptions: string[] = [];
  referenceOptions: string[] = [];
  companyOptions: string[] = [];
  creatorOptions: string[] = [];
  dateCreatedOptions: string[] = [];

  selectedFilters: { [key: string]: string[] } = {
    code_number: [],
    reference: [],
    company: [],
    creator_email: [],
  };

  createdDateRange: { start: string | null; end: string | null } = {
    start: null,
    end: null,
  };

  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';

  private subscriptions = new Subscription();
  private quotesSubject = new BehaviorSubject<Quote[]>([]);
  quotes$: Observable<Quote[]> = this.quotesSubject.asObservable();

  ngOnInit(): void {
    this.loadQuotes();
    this.subscriptions.add(
      this.settingsService.settings$.subscribe((settings) => {
        this.settings = settings ?? undefined;
      }),
    );

    this.subscriptions.add(
      this.searchSubject
        .pipe(debounceTime(300), distinctUntilChanged())
        .subscribe((term) => {
          this.searchTerm = term;
          this.first = 0;
          this.loadQuotes();
        }),
    );
  }

  get isApprovalFlowEnabled(): boolean {
    return this.settings?.approval_flow_enabled ?? false;
  }

  getApprovalStatusLabel(status: string): string {
    switch (status) {
      case 'NOT_REQUESTED':
        return 'Draft';
      case 'REQUESTED':
        return 'Pending Review';
      case 'APPROVED':
        return 'Approved';
      case 'CHANGES_REQUESTED':
        return 'Changes Requested';
      default:
        return status;
    }
  }

  private loadQuotes(): void {
    const filters = { ...this.selectedFilters };
    if (this.createdDateRange.start) {
      filters['created_date_after'] = [this.createdDateRange.start];
    }
    if (this.createdDateRange.end) {
      filters['created_date_before'] = [this.createdDateRange.end];
    }

    this.subscriptions.add(
      this.quotesService
        .getQuotes$(
          this.showArchivedQuotes,
          this.rows,
          this.first,
          this.searchTerm,
          filters,
          this.sortColumn,
          this.sortDirection,
        )
        .subscribe({
          next: (response) => {
            this.totalRecords = response.count;

            this.numberOptions = response.options.code_number;
            this.referenceOptions = response.options.reference;
            this.companyOptions = response.options.company;
            this.dateCreatedOptions = response.options.created_date;
            this.creatorOptions = response.options.creator_email;

            this.quotesSubject.next(response.results);
          },
          error: (err) => console.error('Error fetching quotes', err),
        }),
    );
  }

  onSortChange(event: { column?: string; direction?: 'asc' | 'desc' }) {
    this.sortColumn = event.column;
    this.sortDirection = event.direction;
    this.loadQuotes();
  }

  onFilterChanged(columnKey: string, selected: string[]) {
    this.selectedFilters[columnKey] = selected;
    this.first = 0;
    this.loadQuotes();
  }

  onCreatedDateRangeChange(range: {
    start: string | null;
    end: string | null;
  }) {
    this.createdDateRange = range;
    this.first = 0;
    this.loadQuotes();
  }

  clearAllFilters() {
    this.searchSubject.next(this.searchTerm);
    this.selectedFilters = {
      code_number: [],
      reference: [],
      company: [],
      creator_email: [],
    };
    this.createdDateRange = { start: null, end: null };
    this.first = 0;
    this.loadQuotes();
  }

  newQuote() {
    if (this.creatingQuote()) {
      return;
    }

    this.creatingQuote.set(true);
    this.subscriptions.add(
      this.quotesService.createQuote$().subscribe({
        next: (quote: Quote) => {
          this.router
            .navigate(['/quote', quote.id, quote.current_revision.number])
            .then((navigated) => {
              // A cancelled navigation (e.g. by a guard) resolves to false.
              if (!navigated) {
                this.creatingQuote.set(false);
              }
            })
            .catch(() => {
              this.creatingQuote.set(false);
            });
        },
        error: (err) => {
          this.creatingQuote.set(false);
          console.error('Error fetching data', err);
        },
      }),
    );
  }

  showArchived() {
    this.first = 0;
    this.loadQuotes();
  }

  onPageChange(event: PaginatorState) {
    this.first = event.first ?? 0;
    this.rows = event.rows ?? 20;
    this.loadQuotes();
  }

  archiveQuote(quote: Quote) {
    this.subscriptions.add(
      this.quotesService
        .patchQuote$(quote.id, { archived_at: new Date().toISOString() })
        .subscribe({
          next: () => {
            this.loadQuotes();
          },
          error: (err) => console.error('Error archiving quote', err),
        }),
    );
  }

  unarchiveQuote(quote: Quote) {
    this.subscriptions.add(
      this.quotesService
        .patchQuote$(quote.id, { archived_at: null })
        .subscribe({
          next: () => {
            this.loadQuotes();
          },
          error: (err) => console.error('Error unarchiving quote', err),
        }),
    );
  }

  confirmDeleteQuote(quote: Quote) {
    this.confirmationService.confirm({
      header: 'Delete Quote',
      message: `Are you sure you want to delete this quote? This action is irreversible.`,
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.subscriptions.add(
          this.quotesService.deleteQuote$(quote.id).subscribe({
            next: () => {
              this.loadQuotes();
            },
            error: (err) => console.error('Error deleting quote', err),
          }),
        );
      },
    });
  }

  toggleExpand(quoteId: string, event: Event) {
    event.stopPropagation();
    if (this.expandedQuotes.has(quoteId)) {
      this.expandedQuotes.delete(quoteId);
    } else {
      this.expandedQuotes.add(quoteId);
    }
  }

  isExpanded(quoteId: string): boolean {
    return this.expandedQuotes.has(quoteId);
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  routeToQuotePage(quoteId: string, currentRevisionNumber: number) {
    this.router.navigate(['/quote', quoteId, currentRevisionNumber]).then();
  }
}
